import Async from "components/DesignSystem/AsyncComponents/Async";
import { SettingsAccordion } from "./SettingsAccordion";
import { SectionHeader, SettingRow } from "./BooksSettings";
import { Settings } from "components/icons/Navbar/Settings";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  useGetInvoiceSettingsQuery,
  useCreateOrUpdateInvoiceSettingsMutation,
} from "store/apis/invoices";
import { useModal } from "hooks/useModal";
import { Button } from "components/DesignSystem/Button/Button";
import {
  ConfigureReminderEmail,
  emailReminderTimes,
  EmailReminderTimesType,
} from "components/InvoiceSettings/ConfigureReminderEmail";
import { Formik, Form } from "formik";
import { useToast } from "hooks/useToast";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { LogoUpload } from "components/InvoiceSettings/LogoUpload";
import { PaymentInstructionsModal } from "components/InvoiceSettings/PaymentInstructionsModal";
import { InvoiceFooterModal } from "components/InvoiceSettings/InvoiceFooterModal";
import { SettingActionButton } from "components/InvoiceSettings/SettingActionButton";
import { DocumentIcon } from "components/icons/DocumentIcon";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { AddressComponents } from "components/AddressComponent/AddressComponent";
import { useState } from "react";
import { AddressAutofill } from "types/Models/addressAutofill";
import { AuthoriseEmail } from "components/InvoiceSettings/AuthoriseEmail";
import { PlusIcon } from "components/icons/PlusIcon";
import { MultiValue, SingleValue } from "react-select";
import { BackendError } from "types/utils/error";
import { ConditionalLink } from "components/conditionalLink";
import { object, string as yupString } from "yup";
import { EditPencil } from "components/icons/EditPencil";
import { SmallBellIcon } from "components/icons/LeftNav/SmallBellIcon";
import { Entity } from "components/icons/Navbar/Entity";
import { Payment } from "components/icons/Payment";
import { EmailPreferencesModal } from "components/InvoiceSettings/EmailPreferencesModal";
import { GreentickGreenCircle } from "components/icons/GreentickGreenCircle";
import { Mail } from "components/icons/Mail";
import { usePageTitle } from "hooks/usePageTitle";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";

export const InvoiceSettings = ({ entityId }: { entityId: string }) => {
  usePageTitle("Invoice Settings");
  const group = useCurrentGroupContext();
  const { successToast, alertToast } = useToast();
  const {
    data: invoiceSetting,
    isLoading,
    isSuccess,
  } = useGetInvoiceSettingsQuery(
    {
      groupId: group?.uuid!,
      entityId,
    },
    {
      skip: !group?.uuid || !entityId,
    }
  );

  const [createInvoiceSetting] = useCreateOrUpdateInvoiceSettingsMutation();

  const reminderSettingModal = useModal();
  const paymentInfoModal = useModal();
  const invoiceFooterModal = useModal();
  const authoriseEmailModal = useModal();
  const emailPreferencesModal = useModal();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE
  );

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [updatingField, setUpdatingField] = useState<string | null>(null);

  const selectedEntity = group.entities?.find(
    (e) => e.uuid === invoiceSetting?.entity_id
  );

  const companyDetailsValidationSchema = object({
    email: yupString()
      .email("Please enter a valid email address")
      .required("Email is required"),
    invoice_prefix: yupString().required("Invoice prefix is required"),
  });

  const handleCompanyDetailsSubmit = async (values: {
    email: string;
    invoice_prefix: string;
    entity_id?: string;
    address?: { uuid?: string };
  }) => {
    if (group?.uuid) {
      try {
        await createInvoiceSetting({
          groupId: group.uuid,
          entityId: entityId,
          payload: {
            ...invoiceSetting,
            email: values.email,
            invoice_prefix: values.invoice_prefix,
            entity_id: values.entity_id,
            autofill_address_id: values.address?.uuid,
            // @ts-ignore
            address: undefined,
          },
        }).unwrap();

        const fieldMessages: Record<string, string> = {
          email: "Email updated successfully",
          invoice_prefix: "Invoice prefix updated successfully",
          entity_id: "Entity updated successfully",
          address: "Address updated successfully",
        };

        const message = updatingField
          ? fieldMessages[updatingField]
          : "Company Details updated successfully";

        successToast({ message });
        setUpdatingField(null);
      } catch (error) {
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
        setUpdatingField(null);
      }
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (group?.uuid) {
      try {
        await createInvoiceSetting({
          groupId: group.uuid,
          entityId: entityId,
          payload: {
            ...invoiceSetting,
            logo: file,
            // @ts-ignore
            address: undefined,
          },
        }).unwrap();
        setLogoFile(file);
        successToast({ message: "Logo updated successfully" });
      } catch (error) {
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
      }
    }
  };

  const logoSrc = logoFile
    ? URL.createObjectURL(logoFile)
    : invoiceSetting?.logo_url;
  const hasPaymentInfo = Boolean(invoiceSetting?.payment_info);
  const hasInvoiceFooter = Boolean(invoiceSetting?.invoice_footer);

  const invoiceReminderSetting = invoiceSetting?.reminder_settings;
  const disableReminder =
    !Boolean(invoiceSetting?.reminder_settings) &&
    (!invoiceSetting?.email ||
      !invoiceSetting?.entity_id ||
      !invoiceSetting?.invoice_prefix);

  return (
    <Async.Root isLoading={isLoading} isSuccess={isSuccess} isEmpty={false}>
      <Async.Success>
        <div className="t-pb-20">
          <SettingsAccordion.Root defaultValue={["COMPANY_DETAILS"]}>
            <SettingsAccordion.Item value="COMPANY_DETAILS">
              <SettingsAccordion.Trigger>
                <SectionHeader
                  icon={<Entity className="t-size-3.5" />}
                  title="Company Details"
                />
              </SettingsAccordion.Trigger>

              <SettingsAccordion.Content>
                <Formik
                  initialValues={{
                    email: invoiceSetting?.email || "",
                    invoice_prefix: invoiceSetting?.invoice_prefix || "",
                    entity_id: invoiceSetting?.entity_id || "",
                    address: invoiceSetting?.address || { uuid: "" },
                  }}
                  validationSchema={companyDetailsValidationSchema}
                  onSubmit={handleCompanyDetailsSubmit}
                  enableReinitialize
                  validateOnChange={false}
                  validateOnBlur
                >
                  {({ values, setFieldValue, validateField, submitForm }) => {
                    const handleEntityChange = (
                      value: SingleValue<OptionData>
                    ) => {
                      if (!value?.value || !hasWritePermission) return;

                      setFieldValue("entity_id", value.value);
                      setUpdatingField("entity_id");
                      submitForm();
                    };

                    const handleEmailBlur = async () => {
                      if (!hasWritePermission) return;
                      await validateField("email");

                      if (values.email !== invoiceSetting?.email) {
                        setUpdatingField("email");
                        submitForm();
                      }
                    };

                    return (
                      <Form>
                        <SettingRow
                          title="Entity"
                          description="The legal entity associated with your invoices"
                        >
                          <div className="t-w-56 sm:t-w-full">
                            <Combobox
                              isDisabled={
                                Boolean(invoiceSetting?.entity_id) ||
                                !hasWritePermission
                              }
                              options={group.entities?.map((entity) => ({
                                label: entity.name,
                                value: entity.uuid,
                              }))}
                              withForm={false}
                              value={
                                selectedEntity
                                  ? {
                                      label: selectedEntity.name,
                                      value: selectedEntity.uuid,
                                    }
                                  : null
                              }
                              size="regular"
                              placeholder="Select your entity"
                              onChange={(value) =>
                                handleEntityChange(
                                  value as SingleValue<OptionData>
                                )
                              }
                              actions={
                                <ConditionalLink target="_blank" to="/entities">
                                  <Button customType="secondary">
                                    Add Entity
                                  </Button>
                                </ConditionalLink>
                              }
                            />
                          </div>
                        </SettingRow>

                        <SettingRow
                          title="Email Id"
                          description="This will be set as CC & reply-to email"
                        >
                          <div className="t-w-56 sm:t-w-full">
                            <TextInput
                              type="email"
                              name="email"
                              placeholder="Enter email"
                              customSize="regular"
                              disabled={!hasWritePermission}
                              onBlur={handleEmailBlur}
                            />
                          </div>
                        </SettingRow>

                        <SettingRow title="Business Address" align="top">
                          <div className="t-w-96 sm:t-w-full">
                            <AddressComponents
                              entityId={entityId}
                              groupId={group.uuid}
                              selectedAddressId={invoiceSetting?.address?.uuid}
                              onAddressSelect={(
                                address: AddressAutofill | null
                              ) => {
                                if (!hasWritePermission) return;
                                setFieldValue("address", address);
                                setUpdatingField("address");
                                submitForm();
                              }}
                              isDisabled={!hasWritePermission}
                              hideClear
                              required={false}
                              label=""
                            />
                          </div>
                        </SettingRow>

                        <SettingRow
                          title="Email for sending invoice"
                          description={invoiceSetting?.connected_gmail}
                        >
                          <div>
                            <LockedUi
                              feature={WRITE_PERMISSION_KEYS.REVENUE}
                              blockedUi={
                                <LockedButton
                                  text={
                                    invoiceSetting?.connected_gmail
                                      ? "Edit"
                                      : "Add"
                                  }
                                />
                              }
                            >
                              <Button
                                type="button"
                                customType="secondary"
                                onClick={authoriseEmailModal.open}
                                leftIcon={
                                  invoiceSetting?.connected_gmail ? (
                                    <EditPencil className="t-size-4" />
                                  ) : (
                                    <PlusIcon size="12" />
                                  )
                                }
                                size="small"
                              >
                                {invoiceSetting?.connected_gmail
                                  ? "Edit"
                                  : "Add"}
                              </Button>
                            </LockedUi>
                          </div>
                        </SettingRow>

                        <SettingRow
                          title="Logo"
                          description="Upload a horizontal logo, ideally 1200×400px (3:1) for a sharp look on high-resolution screens. Other sizes work too."
                        >
                          <LogoUpload
                            src={logoSrc}
                            alt={invoiceSetting?.entity_name}
                            onDrop={
                              hasWritePermission ? handleLogoUpload : undefined
                            }
                          />
                        </SettingRow>

                        <SettingRow
                          title="Invoice Prefix"
                          description={`Invoice number preview: ${values.invoice_prefix}-000001`}
                        >
                          <div className="t-w-56 sm:t-w-full">
                            <TextInput
                              type="text"
                              name="invoice_prefix"
                              placeholder="Enter prefix"
                              customSize="regular"
                              disabled={!hasWritePermission}
                              onBlur={async () => {
                                if (!hasWritePermission) return;
                                await validateField("invoice_prefix");
                                if (
                                  values.invoice_prefix !==
                                  invoiceSetting?.invoice_prefix
                                ) {
                                  setUpdatingField("invoice_prefix");
                                  submitForm();
                                }
                              }}
                            />
                          </div>
                        </SettingRow>
                      </Form>
                    );
                  }}
                </Formik>
              </SettingsAccordion.Content>
            </SettingsAccordion.Item>

            {/* Email Preferences Section */}
            <SettingsAccordion.Item value="EMAIL_PREFERENCES">
              <SettingsAccordion.Trigger>
                <SectionHeader
                  icon={<Mail className="t-size-3.5" />}
                  title="Email Preferences"
                  toolTipText="When sending invoice to customers, we will add these email addresses as CC. If needed, you can remove when drafting email."
                />
              </SettingsAccordion.Trigger>

              <SettingsAccordion.Content>
                <SettingRow
                  description="Team emails"
                  info={
                    <div className="t-flex t-flex-col t-gap-1">
                      <div className="t-text-body-sm t-text-text-100">
                        {invoiceSetting?.cc_recipient_emails &&
                        invoiceSetting.cc_recipient_emails.length > 0
                          ? invoiceSetting.cc_recipient_emails.join(", ")
                          : "No team emails added"}
                      </div>

                      <div className="t-flex t-items-center t-gap-1 t-text-body-sm-italic t-text-text-30">
                        <GreentickGreenCircle color="green" />
                        <span className="t-italic">
                          Auto-save enabled for customer emails (from "CC" input
                          field)
                        </span>
                      </div>
                    </div>
                  }
                >
                  <div className="t-flex t-items-center">
                    <LockedUi
                      feature={WRITE_PERMISSION_KEYS.REVENUE}
                      blockedUi={<LockedButton text="Edit" />}
                    >
                      <Button
                        customType="secondary"
                        onClick={emailPreferencesModal.open}
                        leftIcon={<EditPencil className="t-size-4" />}
                        size="small"
                      >
                        Edit
                      </Button>
                    </LockedUi>
                  </div>
                </SettingRow>
              </SettingsAccordion.Content>
            </SettingsAccordion.Item>

            {/* Reminders Section */}
            <SettingsAccordion.Item value="REMINDERS">
              <SettingsAccordion.Trigger>
                <SectionHeader
                  icon={<SmallBellIcon className="t-size-3.5" />}
                  title="Reminders"
                />
              </SettingsAccordion.Trigger>

              <SettingsAccordion.Content>
                <SettingRow
                  description="Send email reminders for unpaid invoices around due date"
                  info={
                    invoiceReminderSetting && (
                      <div className="t-text-body-sm t-text-text-100">
                        <ul className="t-mb-0">
                          {invoiceReminderSetting?.reminder_setting_types
                            .split(",")
                            .map((time) => (
                              <li key={time}>
                                {
                                  emailReminderTimes[
                                    time as EmailReminderTimesType
                                  ].label
                                }
                              </li>
                            ))}
                        </ul>
                      </div>
                    )
                  }
                >
                  <div className="t-flex t-flex-col t-items-end t-gap-2">
                    <LockedUi
                      feature={WRITE_PERMISSION_KEYS.REVENUE}
                      blockedUi={
                        <LockedButton
                          text={invoiceReminderSetting ? "Edit" : "Add"}
                        />
                      }
                    >
                      <Button
                        customType="secondary"
                        onClick={reminderSettingModal.open}
                        disabled={disableReminder}
                        leftIcon={
                          invoiceReminderSetting ? (
                            <EditPencil className="t-size-4" />
                          ) : (
                            <PlusIcon size="12" />
                          )
                        }
                        size="small"
                      >
                        {invoiceReminderSetting ? "Edit" : "Add"}
                      </Button>
                    </LockedUi>
                  </div>
                </SettingRow>
              </SettingsAccordion.Content>
            </SettingsAccordion.Item>

            {/* Payment Instructions Section */}
            <SettingsAccordion.Item value="PAYMENT_INSTRUCTIONS">
              <SettingsAccordion.Trigger>
                <SectionHeader
                  icon={<Payment className="t-size-3.5" />}
                  title="Payment Instructions"
                />
              </SettingsAccordion.Trigger>

              <SettingsAccordion.Content>
                <SettingRow description="Payment Instructions will be added in notes section for all the invoices and emails">
                  <SettingActionButton
                    isEdit={hasPaymentInfo}
                    onClick={paymentInfoModal.open}
                  />
                </SettingRow>
              </SettingsAccordion.Content>
            </SettingsAccordion.Item>

            <SettingsAccordion.Item value="INVOICE_FOOTER">
              <SettingsAccordion.Trigger>
                <SectionHeader
                  icon={<DocumentIcon className="t-size-3.5" />}
                  title="Invoice Footer"
                />
              </SettingsAccordion.Trigger>

              <SettingsAccordion.Content>
                <SettingRow description="A footer text will be added to all pages in the invoice">
                  <SettingActionButton
                    isEdit={hasInvoiceFooter}
                    onClick={invoiceFooterModal.open}
                  />
                </SettingRow>
              </SettingsAccordion.Content>
            </SettingsAccordion.Item>
          </SettingsAccordion.Root>
        </div>

        {authoriseEmailModal.isOpen && (
          <AuthoriseEmail
            isOpen={authoriseEmailModal.isOpen}
            close={authoriseEmailModal.close}
          />
        )}

        {reminderSettingModal.isOpen && (
          <ConfigureReminderEmail
            isOpen={reminderSettingModal.isOpen}
            close={reminderSettingModal.close}
            invoiceSetting={invoiceSetting}
          />
        )}

        {emailPreferencesModal.isOpen && (
          <EmailPreferencesModal
            isOpen={emailPreferencesModal.isOpen}
            close={emailPreferencesModal.close}
            invoiceSetting={invoiceSetting}
          />
        )}

        {paymentInfoModal.isOpen && invoiceSetting && (
          <PaymentInstructionsModal
            isOpen={paymentInfoModal.isOpen}
            close={paymentInfoModal.close}
            entityId={entityId}
            invoiceSetting={invoiceSetting}
          />
        )}

        {invoiceFooterModal.isOpen && invoiceSetting && (
          <InvoiceFooterModal
            isOpen={invoiceFooterModal.isOpen}
            close={invoiceFooterModal.close}
            entityId={entityId}
            invoiceSetting={invoiceSetting}
          />
        )}
      </Async.Success>
    </Async.Root>
  );
};
